import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Clock, ImageIcon, Plus } from 'lucide-react'
import { PublicMenuItem } from '../../core/models/menu'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import { useCartStore } from '../../core/stores/cartStore'
import Button from '../../components/ui/Button'

interface MenuItemCardProps {
  item: PublicMenuItem
  onAddToCart?: (item: PublicMenuItem, quantity: number) => void
}

export default function MenuItemCard({ item, onAddToCart }: MenuItemCardProps) {
  const [imageError, setImageError] = useState(false)
  const { isRTL, t } = useTranslation()
  const { color, darker } = useBusinessColor()
  const { addItem } = useCartStore()
  const { slug } = useParams()

  const handleAddToCart = () => {
    if (item.isAvailable) {
      if (onAddToCart) {
        onAddToCart(item, 1)
      } else if (slug) {
        addItem(item, 1, slug)
      }
    }
  }

  return (
    <div 
      className={`group bg-white rounded-xl border border-stone-200 hover:shadow-md transition-all duration-200 overflow-hidden ${!item.isAvailable ? 'opacity-60' : ''}`}
      onMouseEnter={(e) => {
        if (item.isAvailable) {
          e.currentTarget.style.borderColor = color
        }
      }}
      onMouseLeave={(e) => {
        if (item.isAvailable) {
          e.currentTarget.style.borderColor = '#e7e5e4'
        }
      }}
    >
      <div className="flex gap-3 p-3">
        {/* Image - Small Square */}
        <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200">
          {item.imageUrl && !imageError ? (
            <img
              src={item.imageUrl}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-stone-400" />
            </div>
          )}
          
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
          )}

          {/* Like Button */}
          {/* <button
            onClick={toggleLike}
            className={`absolute top-1 ${isRTL ? 'left-1' : 'right-1'} w-5 h-5 rounded-full flex items-center justify-center transition-all ${
              isLiked 
                ? 'bg-red-500 text-white' 
                : 'bg-white/90 text-stone-600 hover:bg-white'
            }`}
          >
            <Heart className={`w-3 h-3 ${isLiked ? 'fill-current' : ''}`} />
          </button> */}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          {/* Title and Description */}
          <div className="flex-1">
            <h3 className="font-semibold text-stone-900 text-sm leading-tight mb-1 line-clamp-1">
              {item.name}
            </h3>
            {item.description && (
              <p className="text-stone-600 text-xs leading-relaxed line-clamp-1 mb-2">
                {item.description}
              </p>
            )}
          </div>

          {/* Bottom Row: Price and Actions */}
          <div className="flex items-center justify-between gap-2">
            {/* Price */}
            <span 
              className="text-base font-bold whitespace-nowrap"
              style={{ color }}
            >
              {item.formattedPrice}
            </span>

            {/* Add Button */}
            {item.isAvailable ? (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddToCart()
                }}
                variant="primary"
                className="h-7 px-3 text-xs font-semibold rounded-lg text-white shadow-sm flex-shrink-0"
                style={{
                  background: `linear-gradient(to right, ${color}, ${darker})`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${darker}, ${color})`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${color}, ${darker})`
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('addToCart')}
              </Button>
            ) : (
              <span className="text-xs text-stone-500">
                {t('unavailable')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
